import type { LibraryBlockDef } from '../types';

export type PatientFieldTargetKind = 'text' | 'date' | 'number' | 'choice';

export interface PatientFieldTarget {
  /** Write-plan path, e.g. `patient.first_name` — what the backend maps the answer to. */
  path: string;
  label: string;
  group: string;
  kind: PatientFieldTargetKind;
  /** Choice targets: the values the PMS accepts (from the catalog). */
  options?: string[];
}

/**
 * The backend's write-back fields (its `patient_*` / `sb_*` list), as the write-plan paths it
 * reports. Only scalar ones: `patient_address` is split into its parts (the catalog only says
 * `patient.address_*`), plus `patient.address` — the whole address as one answer, which the backend
 * splits on approve and joins into the block's `prefill` for the patient; allergies, conditions, medications and insurance need catalog ids or the
 * insurance decision queue, so a free-typed basic answer cannot target them. The catalog also serves
 * `patient_marital_status` and `patient_ssn_last4`, which the backend does not write back.
 */
const KNOWN_TARGETS: readonly Omit<PatientFieldTarget, 'options'>[] = [
  { path: 'patient.first_name', label: 'First Name', group: 'Name & identity', kind: 'text' },
  { path: 'patient.middle_initial', label: 'Middle Initial', group: 'Name & identity', kind: 'text' },
  { path: 'patient.last_name', label: 'Last Name', group: 'Name & identity', kind: 'text' },
  { path: 'patient.preferred_name', label: 'Preferred Name', group: 'Name & identity', kind: 'text' },
  { path: 'patient.title', label: 'Title', group: 'Name & identity', kind: 'choice' },
  { path: 'patient.name_suffix', label: 'Suffix', group: 'Name & identity', kind: 'choice' },
  { path: 'patient.date_of_birth', label: 'Date of Birth', group: 'Name & identity', kind: 'date' },
  { path: 'patient.gender', label: 'Gender', group: 'Name & identity', kind: 'choice' },
  { path: 'patient.preferred_language', label: 'Preferred Language', group: 'Name & identity', kind: 'choice' },
  { path: 'patient.email', label: 'Email Address', group: 'Contact', kind: 'text' },
  { path: 'patient.mobile_phone', label: 'Mobile Phone', group: 'Contact', kind: 'text' },
  { path: 'patient.home_phone', label: 'Home Phone', group: 'Contact', kind: 'text' },
  { path: 'patient.work_phone', label: 'Work Phone', group: 'Contact', kind: 'text' },
  { path: 'patient.contact_method', label: 'Contact Method', group: 'Contact', kind: 'choice' },
  { path: 'patient.address', label: 'Full Address', group: 'Address', kind: 'text' },
  { path: 'patient.address_line1', label: 'Address Line 1', group: 'Address', kind: 'text' },
  { path: 'patient.address_line2', label: 'Address Line 2', group: 'Address', kind: 'text' },
  { path: 'patient.address_city', label: 'City', group: 'Address', kind: 'text' },
  { path: 'patient.address_state', label: 'State', group: 'Address', kind: 'text' },
  { path: 'patient.address_postal_code', label: 'Postal Code', group: 'Address', kind: 'text' },
  { path: 'patient.family_size', label: 'Family Size', group: 'Household', kind: 'number' },
  { path: 'patient.income', label: 'Annual Income', group: 'Household', kind: 'number' },
  { path: 'patient.referral_source', label: 'Referral Source', group: 'Referral', kind: 'text' },
];

/** Which targets each basic block can write into as-is. */
const KIND_BY_BASIC_TYPE: Record<string, PatientFieldTargetKind> = {
  text_input: 'text',
  textarea: 'text',
  date_picker: 'date',
  number_input: 'number',
  dropdown: 'choice',
  radio: 'choice',
  checkbox: 'choice',
  checkbox_group: 'choice',
};

/** Every patient field a basic block can update, with the PMS's accepted values for choice fields. */
export function buildPatientFieldTargets(library: LibraryBlockDef[]): PatientFieldTarget[] {
  // A patient-info catalog entry's `desc` is its write-plan path; its `options` are what the PMS accepts.
  const optionsByPath = new Map<string, string[]>();
  for (const def of library) {
    if (def.source === 'patient' && def.options?.length) optionsByPath.set(def.desc.trim(), def.options);
  }

  return KNOWN_TARGETS.map((target) => {
    const options = target.kind === 'choice' ? optionsByPath.get(target.path) : undefined;
    return options ? { ...target, options } : { ...target };
  });
}

export function patientFieldTargetKind(basicTypeId: string): PatientFieldTargetKind | undefined {
  return KIND_BY_BASIC_TYPE[basicTypeId];
}

/** Targets a basic block of `basicTypeId` may update, in display order. */
export function listPatientFieldTargets(
  library: LibraryBlockDef[],
  basicTypeId: string,
): PatientFieldTarget[] {
  const kind = patientFieldTargetKind(basicTypeId);
  if (!kind) return [];
  return buildPatientFieldTargets(library).filter((target) => target.kind === kind);
}

/** Author options the PMS would not accept for a choice target (case-insensitive). */
export function unsupportedChoiceOptions(options: string[], target: PatientFieldTarget): string[] {
  if (target.kind !== 'choice' || !target.options?.length) return [];
  const accepted = new Set(target.options.map((option) => option.trim().toLowerCase()));
  return options.filter((option) => option.trim() && !accepted.has(option.trim().toLowerCase()));
}
